import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Checkbox,
  Dropdown,
  Flex,
  List,
  Popconfirm,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import Modal from '/ui/Modal';

import filesActions from '../../../actions';

import AiIcon from '../../../../../components/AiIcon';

import { message } from '../../../../../utils/message';
import {
  getTerminalIframeUrl,
  normalizeTerminalHost,
} from '../../../utils/atlas';
import {
  downloadFileByUrl,
  getFileDownloadName,
  getFileDownloadUrl,
} from '../../../utils/download';

import FileEditableName from '../FileEditableName';
import NeuroGenerationStatus from '../NeuroGenerationStatus';

import { AppDispatch, ReduxState } from '../../../../../types/app';
import { AiFileItem, AiFileStatus } from '../../../types';

const { Text } = Typography;

type Props = {
  file: AiFileItem;
  iconName: string;
  isCompactActions: boolean;
  removingId: number | null;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  onRemove: (id: number) => void;
  onReplace: (id: number) => void;
  onAtlasIframeClose: () => void;
};

const FileListItem = ({
  file,
  iconName,
  isCompactActions,
  removingId,
  selectable = false,
  selected = false,
  onSelectChange,
  onRemove,
  onAtlasIframeClose,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const terminalHost = useSelector(
    (state: ReduxState) => state.Data.config?.terminalHost
  );
  const themeType = useSelector(
    (state: ReduxState) => state.Data.config?.themeType
  );
  const brokerId = useSelector((state: ReduxState) => state.Data.broker?.Id);
  const clientId = useSelector((state: ReduxState) => state.Client.data?.Id);
  const [changingNeuroStatus, setChangingNeuroStatus] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [atlasModalVisible, setAtlasModalVisible] = useState(false);
  const [atlasIframeUrl, setAtlasIframeUrl] = useState<string | null>(null);
  const isBusy = removingId !== null || changingNeuroStatus;
  const isNeuroError = file.Status === AiFileStatus.Error;
  const isNeuroPending = file.Status === AiFileStatus.Pending;
  const isNeuroProcessing = file.Status === AiFileStatus.Processing;
  const isNeuroProcessed = file.Status === AiFileStatus.Processed;
  const isNeuroDraft = file.Status === AiFileStatus.Draft;
  const showNeuroGenerationLabel = isNeuroPending || isNeuroProcessing;
  const shouldShowNeuroStatus =
    isNeuroError || showNeuroGenerationLabel || isNeuroProcessed;
  const neuroStatusTextId = isNeuroError
    ? 'files.card.errorNeuro'
    : 'files.card.processedNeuro';
  const neuroStatusClassName = isNeuroError
    ? 'list-item__processing list-item__processing_error'
    : isNeuroProcessed
      ? 'list-item__processing list-item__processing_processed'
      : 'list-item__processing list-item__processing_generation';
  const hasTerminalHost = useMemo(
    () => Boolean(normalizeTerminalHost(terminalHost)),
    [terminalHost]
  );
  const downloadUrl = useMemo(
    () => getFileDownloadUrl(brokerId, clientId, file.Key, 'csv'),
    [brokerId, clientId, file.Key]
  );
  const neuroDownloadUrl = useMemo(
    () => getFileDownloadUrl(brokerId, clientId, file.Key, 'neuro'),
    [brokerId, clientId, file.Key]
  );

  const getFileStatusIcon = () => {
    if (isNeuroError) {
      return <AiIcon name="error" />;
    }

    if (isNeuroProcessed) {
      return <AiIcon name="success" />;
    }

    return null;
  };

  const closeActionsDropdown = useCallback(() => {
    setActionsDropdownOpen(false);
  }, []);

  const handleDownload = useCallback(() => {
    if (!downloadUrl) {
      return;
    }

    closeActionsDropdown();

    void downloadFileByUrl(
      downloadUrl,
      getFileDownloadName(file.Name, 'csv')
    ).catch((err) => {
      console.error(err);
    });
  }, [closeActionsDropdown, downloadUrl, file.Name]);

  const handleNeuroDownload = useCallback(() => {
    if (!neuroDownloadUrl) {
      return;
    }

    closeActionsDropdown();

    void downloadFileByUrl(
      neuroDownloadUrl,
      getFileDownloadName(file.Name, 'neuro')
    ).catch((err) => {
      console.error(err);
    });
  }, [closeActionsDropdown, neuroDownloadUrl, file.Name]);

  const handleOpenAtlasModal = useCallback(() => {
    const iframeUrl = getTerminalIframeUrl(
      terminalHost,
      file.Id,
      intl.locale,
      themeType,
      { appPath: true, mode: 'mode-neural-generator' }
    );

    if (!iframeUrl) return;

    closeActionsDropdown();
    setAtlasIframeUrl(iframeUrl);
    setAtlasModalVisible(true);
  }, [closeActionsDropdown, file.Id, intl.locale, terminalHost, themeType]);

  const handleCloseAtlasModal = useCallback(() => {
    setAtlasModalVisible(false);
    onAtlasIframeClose();
  }, [onAtlasIframeClose]);

  const handleChangeNeuroStatus = useCallback(async () => {
    if (isBusy) {
      return;
    }

    closeActionsDropdown();
    setChangingNeuroStatus(true);

    try {
      await dispatch(
        filesActions.Files.changeFileStatus(
          file.Id,
          isNeuroPending ? 'Draft' : 'Pending'
        )
      );
    } catch (err) {
      console.error(err);
      message.error(err);
    } finally {
      setChangingNeuroStatus(false);
    }
  }, [closeActionsDropdown, dispatch, file.Id, isBusy, isNeuroPending]);

  const renderActionButtons = (block = false) => {
    const downloadButton = (
      <Button
        block={block}
        className="btn-secondary"
        icon={<AiIcon name="download" />}
        disabled={!downloadUrl}
        onClick={handleDownload}
      >
        <FormattedMessage id="files.card.download" />
      </Button>
    );
    const removeButton = (
      <Popconfirm
        title={intl.formatMessage({ id: 'popConfirm.title' })}
        okText={intl.formatMessage({ id: 'buttons.yes' })}
        cancelText={intl.formatMessage({ id: 'buttons.no' })}
        disabled={isBusy}
        onConfirm={() => onRemove(file.Id)}
      >
        <Button
          block={block}
          danger
          icon={<AiIcon name="delete" />}
          loading={removingId === file.Id}
          disabled={isBusy}
        >
          <FormattedMessage id="files.card.remove" />
        </Button>
      </Popconfirm>
    );
    const changeAtlasButton = (
      <Button
        block={block}
        icon={<AiIcon name="changeAtlas" />}
        disabled={!hasTerminalHost}
        onClick={handleOpenAtlasModal}
      >
        <FormattedMessage id="files.card.changeAtlas" />
      </Button>
    );
    const neuroDraftAction = isNeuroDraft && (
      <Popconfirm
        title={intl.formatMessage({ id: 'popConfirm.title' })}
        okText={intl.formatMessage({ id: 'buttons.yes' })}
        cancelText={intl.formatMessage({ id: 'buttons.no' })}
        disabled={isBusy}
        onConfirm={() => void handleChangeNeuroStatus()}
      >
        <Button
          block={block}
          icon={<AiIcon name="ai" />}
          loading={changingNeuroStatus}
          disabled={isBusy}
        >
          <FormattedMessage id="files.card.addNeuro" />
        </Button>
      </Popconfirm>
    );
    const neuroStatusAction = shouldShowNeuroStatus && (
      <div className="processing__status-actions">
        {showNeuroGenerationLabel ? (
          <NeuroGenerationStatus
            dateUpdate={file.DateUpdate}
            className={neuroStatusClassName}
            labelClassName="list-item__processing-label"
          />
        ) : isNeuroProcessed ? (
          <Button
            className="btn-secondary"
            block={block}
            icon={<AiIcon name="download" />}
            disabled={!neuroDownloadUrl}
            onClick={handleNeuroDownload}
          >
            <FormattedMessage id="files.card.downloadNeuro" />
          </Button>
        ) : (
          <span className={neuroStatusClassName}>
            {getFileStatusIcon()}
            <span className="list-item__processing-label">
              <FormattedMessage id={neuroStatusTextId} />
            </span>
          </span>
        )}
        {isNeuroPending && (
          <Popconfirm
            title={intl.formatMessage({ id: 'popConfirm.title' })}
            okText={intl.formatMessage({ id: 'buttons.yes' })}
            cancelText={intl.formatMessage({ id: 'buttons.no' })}
            disabled={isBusy}
            onConfirm={() => void handleChangeNeuroStatus()}
          >
            <Button
              type="link"
              className="processing__cancel-btn"
              block={block}
              loading={changingNeuroStatus}
              disabled={isBusy}
            >
              <FormattedMessage id="files.card.cancel" />
            </Button>
          </Popconfirm>
        )}
      </div>
    );

    if (block) {
      return (
        <>
          {downloadButton}
          {changeAtlasButton}
          {neuroDraftAction}
          {neuroStatusAction}
          {removeButton}
        </>
      );
    }

    return (
      <>
        {downloadButton}
        {changeAtlasButton}
        {neuroDraftAction}
        {neuroStatusAction}
        {removeButton}
      </>
    );
  };

  return (
    <List.Item
      className={`files-page__list-item${
        selected ? ' files-page__list-item_selected' : ''
      }`}
    >
      <Flex
        className="files-page__list-row"
        align="center"
        justify="space-between"
        gap={16}
      >
        <Flex
          className="files-page__list-info"
          align="center"
          gap={12}
          flex={1}
          style={{ minWidth: 0 }}
        >
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={(event) => onSelectChange?.(event.target.checked)}
            />
          )}
          <span className="files-page__card-icon files-page__card-icon_sm">
            <AiIcon name={iconName} />
          </span>
          <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
            <FileEditableName fileId={file.Id} name={file.Name} />
            <Flex align="center" gap={8}>
              <Text className="files-page__card-date">
                {dayjs.unix(file.DateCreate).format('DD.MM.YYYY')}
              </Text>
              {/*<Button onClick={() => onReplace(file.Id)}>
                <FormattedMessage id="files.card.replaceCsv" />
              </Button>*/}
            </Flex>
          </Flex>
        </Flex>

        {isCompactActions ? (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            open={actionsDropdownOpen}
            onOpenChange={setActionsDropdownOpen}
            popupRender={() => (
              <Flex
                className="files-page__list-actions-dropdown"
                vertical
                gap={8}
              >
                {renderActionButtons(true)}
              </Flex>
            )}
          >
            <Button
              className="files-page__list-actions-trigger btn-dropdown"
              icon={<AiIcon name="more" />}
              size="small"
              aria-label="File actions"
            />
          </Dropdown>
        ) : (
          <Flex className="files-page__list-actions" align="center" gap={15}>
            {renderActionButtons()}
          </Flex>
        )}
      </Flex>
      <Modal
        className="files-page__atlas-modal"
        open={atlasModalVisible}
        onCancel={handleCloseAtlasModal}
        footer={null}
        centered
        destroyOnHidden
        width="100%"
      >
        <iframe
          className="files-page__atlas-iframe"
          src={atlasIframeUrl || undefined}
          title={intl.formatMessage({ id: 'files.card.changeAtlas' })}
        />
      </Modal>
    </List.Item>
  );
};

export default FileListItem;
